// Board thumbnails (spec §17.3): a 1200×630 PNG of the whole board, the size
// link previews use. An editor's browser renders it and uploads it: shortly
// after opening a board whose thumbnail is out of date, every 2 minutes at
// most while they edit, and when the tab is hidden.
import type { Editor } from '../editor.ts';
import { drawScene, paintOrder, sceneBounds } from './scene.ts';

export const THUMB_W = 1200, THUMB_H = 630;
const MAX_BYTES = 300 * 1024;            // the server's limit
const EVERY_MS = 2 * 60_000;
const BACKGROUND = '#F7F7F5';

/** The board as a PNG of `w`×`h`, fitted with a margin; null for an empty board. */
export async function renderBoardPng(editor: Editor, w = THUMB_W, h = THUMB_H): Promise<Blob | null> {
  const objs = [...editor.store.values()];
  const get = (id: string) => editor.store.get(id);
  const bounds = sceneBounds(objs, get, 40);
  if (!bounds) return null;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = BACKGROUND;
  ctx.fillRect(0, 0, w, h);
  const scale = Math.min(w / bounds.w, h / bounds.h);
  const x = bounds.x - (w / scale - bounds.w) / 2;
  const y = bounds.y - (h / scale - bounds.h) / 2;
  drawScene(ctx, paintOrder(objs), { get, layouts: editor.layouts, images: editor.images }, { x, y, scale });
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

/**
 * Keeps a board's thumbnail current while an editor has it open. `stale`: the
 * saved one is older than the board's content (or missing). Returns a stop function.
 */
export function keepThumbnailFresh(editor: Editor, boardId: string, stale: boolean): () => void {
  let changed = stale;
  let sending = false;
  const off = editor.events.on('history', () => { changed = true; }); // local edits, undo and redo
  const send = async () => {
    if (!changed || sending || editor.readOnly) return;
    sending = true;
    changed = false;
    try {
      let png = await renderBoardPng(editor);
      // Busy boards with photos can come out large: half the size still makes a fine preview.
      if (png && png.size > MAX_BYTES) png = await renderBoardPng(editor, THUMB_W / 2, THUMB_H / 2);
      if (png && png.size <= MAX_BYTES) {
        await fetch(`/api/boards/${encodeURIComponent(boardId)}/thumbnail`, {
          method: 'PUT', credentials: 'same-origin', headers: { 'Content-Type': 'image/png' }, body: png,
        });
      }
    } catch {
      changed = true; // try again next time
    } finally {
      sending = false;
    }
  };
  const first = window.setTimeout(() => void send(), 3000);
  const timer = window.setInterval(() => void send(), EVERY_MS);
  const hidden = () => { if (document.visibilityState === 'hidden') void send(); };
  document.addEventListener('visibilitychange', hidden);
  return () => {
    clearTimeout(first);
    clearInterval(timer);
    document.removeEventListener('visibilitychange', hidden);
    off();
  };
}
